import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Calendar as CalendarIcon, Clock, Mail, Phone, User } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

const bookingSchema = z.object({
  name: z.string().trim().min(2, "Name must be at least 2 characters").max(100, "Name is too long"),
  email: z.string().trim().email("Invalid email address").max(255, "Email is too long"),
  phone: z.string().trim().min(10, "Phone number must be at least 10 digits").max(20, "Phone number is too long").regex(/^[+\d\s()-]+$/, "Invalid phone format"),
  message: z.string().trim().max(2000, "Message is too long").optional(),
});

const contactSchema = z.object({
  name: z.string().trim().min(2, "Name must be at least 2 characters").max(100, "Name is too long"),
  email: z.string().trim().email("Invalid email address").max(255, "Email is too long"),
  phone: z.string().trim().min(10, "Phone number must be at least 10 digits").max(20, "Phone number is too long").regex(/^[+\d\s()-]+$/, "Invalid phone format"),
  company: z.string().trim().max(100, "Company name is too long").optional(),
  message: z.string().trim().min(10, "Please provide more details (at least 10 characters)").max(2000, "Message is too long"),
});

type BookingFormData = z.infer<typeof bookingSchema>;
type ContactFormData = z.infer<typeof contactSchema>;

const allPossibleTimeSlots = [
  "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM",
  "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM", "05:00 PM", "05:30 PM"
];

const generateRandomTimeSlots = (date: Date | undefined): string[] => {
  if (!date) return [];
  
  // Use date as seed for consistent random generation
  const seed = date.getDate() + date.getMonth() * 31 + date.getFullYear() * 372;
  const random = (min: number, max: number) => {
    const x = Math.sin(seed + min) * 10000;
    return Math.floor((x - Math.floor(x)) * (max - min + 1)) + min;
  };
  
  // Random amount of slots (3-9)
  const slotsCount = random(3, 9);
  
  // Shuffle and pick random slots
  const shuffled = [...allPossibleTimeSlots].sort(() => {
    const x = Math.sin(seed * allPossibleTimeSlots.length) * 10000;
    return (x - Math.floor(x)) - 0.5;
  });
  
  return shuffled.slice(0, slotsCount).sort((a, b) => {
    const timeA = allPossibleTimeSlots.indexOf(a);
    const timeB = allPossibleTimeSlots.indexOf(b);
    return timeA - timeB;
  });
};

const BookConsultation = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>();
  
  const availableTimeSlots = useMemo(() => generateRandomTimeSlots(selectedDate), [selectedDate]);

  const bookingForm = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
  });

  const contactForm = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onBookingSubmit = async (data: BookingFormData) => {
    if (!selectedDate || !selectedTime) {
      toast({
        title: "Selection Required",
        description: "Please select both a date and time slot",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: result, error } = await supabase.functions.invoke('submit-consultation', {
        body: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          message: data.message || null,
          preferred_date: selectedDate.toISOString().split('T')[0],
          preferred_time: selectedTime,
          request_type: 'booking'
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Consultation Booked!",
        description: `Your consultation is scheduled for ${format(selectedDate, "PPP")} at ${selectedTime}. Check your email for confirmation.`,
      });
      bookingForm.reset();
      setSelectedDate(undefined);
      setSelectedTime(undefined);
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Booking submission error:", error);
      }
      toast({
        title: "Error",
        description: "Failed to submit booking. Please try again.",
        variant: "destructive",
      });
    }
  };

  const onContactSubmit = async (data: ContactFormData) => {
    try {
      const { data: result, error } = await supabase.functions.invoke('submit-consultation', {
        body: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company || null,
          message: data.message,
          request_type: 'contact'
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Request Received!",
        description: "We'll reach out to you shortly. Check your email for confirmation.",
      });
      contactForm.reset();
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Contact submission error:", error);
      }
      toast({
        title: "Error",
        description: "Failed to send request. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-8"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <div className="max-w-6xl mx-auto space-y-16">
          {/* Booking Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-primary bg-clip-text text-transparent">
              Book Your Free Consultation
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Select a date and time that works for you, and let's discuss how we can transform your business.
            </p>
          </div>

          <Card className="p-8 bg-card/80 backdrop-blur-sm border-2 border-border/50">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <CalendarIcon className="h-6 w-6 text-primary" />
              Schedule Your Consultation
            </h2>

            <div className="grid lg:grid-cols-2 gap-8 mb-8">
              {/* Calendar */}
              <div className="space-y-4">
                <Label className="text-lg">Select a Date</Label>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => {
                      // Disable past dates
                      if (date < new Date()) return true;
                      
                      // Disable weekends
                      if (date.getDay() === 0 || date.getDay() === 6) return true;
                      
                      // Randomly disable ~40% of dates to show busy schedule
                      const seed = date.getDate() + date.getMonth() * 31;
                      return seed % 5 < 2;
                    }}
                    className={cn("rounded-md border pointer-events-auto")}
                  />
                </div>
              </div>

              {/* Time Slots */}
              <div className="space-y-4">
                <Label className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Select a Time Slot
                </Label>
                {selectedDate ? (
                  <div className="grid grid-cols-2 gap-3">
                    {availableTimeSlots.map((time) => (
                      <Button
                        key={time}
                        variant={selectedTime === time ? "default" : "outline"}
                        onClick={() => setSelectedTime(time)}
                        className="h-12"
                      >
                        {time}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg text-center">
                    Please select a date first to see available time slots
                  </p>
                )}

                {selectedDate && selectedTime && (
                  <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <p className="text-sm font-medium">Selected Slot:</p>
                    <p className="text-lg font-bold text-primary">
                      {format(selectedDate, "PPPP")} at {selectedTime}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Booking Form */}
            <form onSubmit={bookingForm.handleSubmit(onBookingSubmit)} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="booking-name">
                    <User className="inline h-4 w-4 mr-2" />
                    Full Name *
                  </Label>
                  <Input
                    id="booking-name"
                    {...bookingForm.register("name")}
                    placeholder="John Doe"
                  />
                  {bookingForm.formState.errors.name && (
                    <p className="text-sm text-destructive">{bookingForm.formState.errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="booking-email">
                    <Mail className="inline h-4 w-4 mr-2" />
                    Email *
                  </Label>
                  <Input
                    id="booking-email"
                    type="email"
                    {...bookingForm.register("email")}
                    placeholder="john@example.com"
                  />
                  {bookingForm.formState.errors.email && (
                    <p className="text-sm text-destructive">{bookingForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="booking-phone">
                    <Phone className="inline h-4 w-4 mr-2" />
                    Phone Number *
                  </Label>
                  <Input
                    id="booking-phone"
                    type="tel"
                    {...bookingForm.register("phone")}
                    placeholder="+1 (555) 000-0000"
                  />
                  {bookingForm.formState.errors.phone && (
                    <p className="text-sm text-destructive">{bookingForm.formState.errors.phone.message}</p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="booking-message">Additional Notes (Optional)</Label>
                  <Textarea
                    id="booking-message"
                    {...bookingForm.register("message")}
                    placeholder="Tell us what you'd like to discuss..."
                    rows={3}
                  />
                </div>
              </div>

              <Button type="submit" size="lg" className="w-full">
                Confirm Booking
              </Button>
            </form>
          </Card>

          {/* Contact Form Section */}
          <Card className="p-8 bg-card/80 backdrop-blur-sm border-2 border-border/50">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Mail className="h-6 w-6 text-primary" />
              Can't Find a Suitable Time? Let Us Reach Out
            </h2>
            <p className="text-muted-foreground mb-6">
              Fill out the form below and we'll contact you to find a time that works best for you.
            </p>

            <form onSubmit={contactForm.handleSubmit(onContactSubmit)} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Full Name *</Label>
                  <Input
                    id="contact-name"
                    {...contactForm.register("name")}
                    placeholder="John Doe"
                  />
                  {contactForm.formState.errors.name && (
                    <p className="text-sm text-destructive">{contactForm.formState.errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    {...contactForm.register("email")}
                    placeholder="john@example.com"
                  />
                  {contactForm.formState.errors.email && (
                    <p className="text-sm text-destructive">{contactForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-phone">Phone Number *</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    {...contactForm.register("phone")}
                    placeholder="+1 (555) 000-0000"
                  />
                  {contactForm.formState.errors.phone && (
                    <p className="text-sm text-destructive">{contactForm.formState.errors.phone.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-company">Company (Optional)</Label>
                  <Input
                    id="contact-company"
                    {...contactForm.register("company")}
                    placeholder="Your Company"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="contact-message">Message *</Label>
                  <Textarea
                    id="contact-message"
                    {...contactForm.register("message")}
                    placeholder="Tell us about your needs and preferred contact times..."
                    rows={4}
                  />
                  {contactForm.formState.errors.message && (
                    <p className="text-sm text-destructive">{contactForm.formState.errors.message.message}</p>
                  )}
                </div>
              </div>

              <Button type="submit" variant="secondary" size="lg" className="w-full">
                Submit Contact Request
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BookConsultation;
